/**
 * lib/listingSubmit.js — Phase 4d Refactor.
 *
 * Extrahiert aus CreateListingPage.jsx (war ~365 Zeilen inline). Erwartet einen
 * ctx-Param mit allen benötigten State-Settern/Werten, damit die Funktion
 * pure bleibt und in mehreren Pages verwendbar wäre.
 */
export async function submitListing(e, ctx) {
    const {
        form, user, id, isEdit, navigate, t, draft,
        cockpitWarned, complianceWarned,
        setSubmitting, setMissingDialogOpen, setBackendValidationErrors,
        setCockpitWarned, setComplianceWarned,
        setWatermarkPreview,
        onPublishedPromptTemplate,
        toast, api,
    } = ctx;
        e.preventDefault();
        // Phase 3.91 — Pflichtfeld-Validierung (lt. User-Anforderung):
        // Titel, Marke, Modell, Fahrzeugtyp, Zustand, Preis B2C, Erstzulassung,
        // Vorbesitzer, KM, Farbbasis, Unfall/Schaden (mind. 1 markiert),
        // Hubraum, kW, Antrieb, Kraftstoff, Getriebe, Bilder.
        // Phase 3.92 — jeder Check trägt ein `selector` für Toast-Klick-Scroll.
        const isMoto = form.category === "motorcycle";
        const isDealerSubmit = user?.role === "b2b";
        // Jun-2026 (User-Wunsch) — Spezial-Modus (Auktion/Sharing): nur Eckdaten
        // (Titel, Marke, Modell) + mind. 1 Bild prüfen; übrige Fahrzeug-Pflichtfelder
        // entfallen, weil das Formular ausgeblendet ist.
        const specialMode = ((form.sale_type === "auction" || form.sale_type === "rental") && isDealerSubmit) || form.sale_type === "pawn";
        const requiredChecks = specialMode ? [
            ["Titel", form.title, '[data-testid="form-title"]'],
            ["Marke", form.brand, '[data-testid="form-brand"]'],
            ["Modell", form.model, '[data-testid="form-model"]'],
        ] : [
            ["Titel",                 form.title,                                                                                                                  '[data-testid="form-title"]'],
            ["Marke",                 form.brand,                                                                                                                  '[data-testid="form-brand"], [data-testid="listing-brand"]'],
            ["Modell",                form.model,                                                                                                                  '[data-testid="form-model"], [data-testid="listing-model"]'],
            [isMoto ? "Fahrzeugtyp" : "Fahrradtyp",  isMoto ? form.vehicle_type : form.bike_type,                                                                  isMoto ? '[data-testid="form-vehicle-type"]' : '[data-testid="form-bike-type"]'],
            ["Zustand",               form.condition,                                                                                                              '[data-testid="listing-condition"]'],
            ["Preis B2C",             form.sale_type === "auction" ? "ok" : form.price,                                                                            '[data-testid="form-price"]'],
            ["Erstzulassung",         form.first_registration_date || form.year,                                                                                   '[data-testid="form-first-registration"], [data-testid="form-year"]'],
            ["Anzahl Vorbesitzer",    form.previous_owners !== "" && form.previous_owners !== null && form.previous_owners !== undefined ? "ok" : "",              '[data-testid="extra-previous-owners"]'],
            ["Kilometerstand",        form.mileage !== "" && form.mileage !== null && form.mileage !== undefined ? "ok" : "",                                      '[data-testid="form-mileage"], input[type="number"][value=""]'],
            ["Farbbasis",             form.color_base,                                                                                                             '[data-testid="listing-color-base"]'],
        ];
        if (isDealerSubmit && !specialMode) {
            requiredChecks.push(
                ["Preis B2B (Gewerblich)", form.price_b2b !== "" && form.price_b2b !== null && form.price_b2b !== undefined ? "ok" : "", '[data-testid="form-price-b2b"]'],
                ["Vermittlungsprovision",  form.referral_commission_eur !== "" && form.referral_commission_eur !== null && form.referral_commission_eur !== undefined ? "ok" : "", '[data-testid="form-referral-commission"]'],
            );
        }
        if (isMoto && !specialMode) {
            requiredChecks.push(
                ["Hubraum (ccm)",  form.displacement, '[data-testid="form-displacement"]'],
                ["Leistung (kW)",  form.power_kw,     '[data-testid="form-power-kw"], [data-testid="listing-power-kw"]'],
                ["Antrieb",        form.drive_type,   '[data-testid="listing-drive-type"]'],
                ["Kraftstoff",     form.fuel,         '[data-testid="listing-fuel"]'],
                ["Getriebe",       form.transmission, '[data-testid="listing-transmission"]'],
            );
        }
        const missing = requiredChecks.filter(([_l, v]) => !v && v !== 0);
        // Bilder: mindestens 1 — Form-State nutzt `images` (siehe ImageGridSection)
        if (!Array.isArray(form.images) || form.images.length === 0) {
            missing.push(["Bilder (mindestens 1)", "", '[data-testid="image-upload-section"], input[type="file"]']);
        }
        if (missing.length > 0) {
            // Mai-2026 — Pflichtfeld-Popup statt Sticky-Banner. Wir öffnen den
            // Dialog mit der Liste; der vorhandene toast-Hin-Springen-Flow bleibt
            // erhalten als zusätzlicher visueller Cue.
            setMissingDialogOpen(true);
            const labels = missing.map((m) => m[0]);
            // Feb-2026 (User-Wunsch 22 May): Toast-Action „Hin springen" entfernt
            // — der MissingFieldsDialog bietet bereits einen besseren Sprung-Flow
            // mit einzelnen Pillen pro Feld. Doppelte Funktion vermieden.
            toast.error(
                `Bitte ausfüllen: ${labels.join(", ")}`,
                {
                    duration: 7000,
                    id: "create-required",
                },
            );
            return;
        }

        // Phase 3.54 — Vertrauens-Warnung: empfohlene Cockpit-mit-Tachostand-Aufnahme prüfen.
        // Kein Hard-Block — beim ersten Submit nur Hinweis, beim zweiten geht es durch.
        const hasCockpitLabel = (form.image_labels || []).some(
            (l) => l && l.toLowerCase().includes("cockpit"),
        );
        if (form.category === "motorcycle" && !specialMode && !hasCockpitLabel && !cockpitWarned) {
            setCockpitWarned(true);
            toast.warning(
                `Hinweis: Es fehlt noch ein Bild mit dem Label „Cockpit mit Tachostand". Käufer vertrauen Inseraten mit Tacho-Foto deutlich mehr — bitte ergänzen oder erneut „Speichern" klicken, um trotzdem zu veröffentlichen.`,
                { duration: 8000, id: "cockpit-warn" },
            );
            return;
        }
        // Phase 4.12 (M2) — Compliance-Check via Gemini (block bei Hard-Verstößen)
        try {
            const { data: comp } = await api.post("/listings/ai-compliance-check", {
                title: form.title || "",
                description: form.description || "",
            });
            if (comp.verdict === "block") {
                const reasons = (comp.issues || []).map((i) => i.explanation || i.phrase).filter(Boolean).slice(0, 4).join("\n• ");
                toast.error(
                    `Anzeige enthält unzulässige Inhalte:\n• ${reasons}\n\nBitte überarbeite Titel/Beschreibung.`,
                    { duration: 12000, id: "compliance-block" },
                );
                return;
            }
            if (comp.verdict === "warning" && !complianceWarned) {
                setComplianceWarned(true);
                const reasons = (comp.issues || []).map((i) => i.explanation || i.phrase).filter(Boolean).slice(0, 3).join("\n• ");
                toast.warning(
                    `Compliance-Hinweis:\n• ${reasons}\n\nDu kannst dein Inserat erneut „Speichern" um trotzdem zu veröffentlichen.`,
                    { duration: 10000, id: "compliance-warn" },
                );
                return;
            }
        } catch (err) {
            // Compliance failure is non-blocking — don't trap the user
            console.warn("Compliance check skipped", err);
        }
        setSubmitting(true);
        try {
            const isAuction = form.sale_type === "auction";
            const payload = {
                ...form,
                price: isAuction ? 0 : Number(form.price || 0),
                // Feb-2026 (Bug-Fix): Falls Inserent nur Erstzulassung eingibt
                // (Datumsfeld), aber nicht das Jahr-Feld nutzt → Year aus dem
                // Datum ableiten, damit das Backend-Pflichtfeld nicht 422 wirft.
                year: Number(form.year) || (form.first_registration_date
                    ? new Date(form.first_registration_date).getFullYear()
                    : new Date().getFullYear()),
                mileage: Number(form.mileage || 0),
                power_kw: form.power_kw ? Number(form.power_kw) : null,
                power_hp: form.power_hp ? Number(form.power_hp) : null,
                displacement: form.displacement ? Number(form.displacement) : null,
                gears: form.gears ? Number(form.gears) : null,
                starting_price: isAuction ? Number(form.starting_price || 0) : null,
                reserve_price: isAuction && form.reserve_price !== "" ? Number(form.reserve_price) : null,
                min_increment: isAuction ? Number(form.min_increment || 50) : null,
                auction_end: isAuction && form.auction_end ? new Date(form.auction_end).toISOString() : null,
                buy_now_price: isAuction && form.buy_now_price ? Number(form.buy_now_price) : null,
                auction_audience: isAuction ? (form.auction_audience || "b2b_only") : null,
                // Jun-2026 (User-Wunsch) — Pfand / Refinanzieren
                pawn_payout_eur: form.sale_type === "pawn" && form.pawn_payout_eur !== "" ? Number(form.pawn_payout_eur) : null,
                pawn_term_months: form.sale_type === "pawn" && form.pawn_term_months !== "" ? parseInt(form.pawn_term_months, 10) : null,
                pawn_buyback_eur: form.sale_type === "pawn" && form.pawn_buyback_eur !== "" ? Number(form.pawn_buyback_eur) : null,
                pawn_usages: form.sale_type === "pawn" && Array.isArray(form.pawn_usages) ? form.pawn_usages : [],
                allow_offers: isAuction ? false : !!form.allow_offers,
                min_offer_amount: !isAuction && form.allow_offers && form.min_offer_amount ? Number(form.min_offer_amount) : null,
                auto_counter_amount: !isAuction && form.allow_offers && form.auto_counter_amount ? Number(form.auto_counter_amount) : null,
                instant_buy_enabled: !isAuction ? !!form.instant_buy_enabled : false,
                missed_call_alerts_enabled: form.missed_call_alerts_enabled !== false,
                allow_tradein: isAuction ? false : !!form.allow_tradein,
                allow_chat: !!form.allow_chat,
                allow_test_ride: !!form.allow_test_ride,
                // Phase 3.51 — Neue Felder
                emission_class: form.emission_class || null,
                image_labels: form.image_labels || [],
                video_url: form.video_url || null,
                first_registration_date: form.first_registration_date || null,
                drive_type: form.drive_type || null,
                sharing_enabled: !!form.sharing_enabled,
                // Phase 3.52 — Farbe + Standort + Fahrzeugdaten + Sharing-Inputs
                color_finish: form.color_finish || null,
                color_base: form.color_base || null,
                street: form.street || null,
                previous_owners: form.previous_owners !== "" ? parseInt(form.previous_owners, 10) : null,
                next_inspection_date: form.next_inspection_date || null,
                warranty_months: form.warranty_months !== "" ? parseInt(form.warranty_months, 10) : null,
                has_service_book: !!form.has_service_book,
                accident_free: !!form.accident_free,
                // Phase 3.89 — Unfall/Schaden-Erweiterung
                is_accident_vehicle: !!form.is_accident_vehicle,
                has_tipover_damage: !!form.has_tipover_damage,
                is_roadworthy: form.is_roadworthy !== false,
                damage_info: form.damage_info ? String(form.damage_info).slice(0, 2000) : null,
                country_of_registration: form.country_of_registration || null,
                vehicle_type: form.vehicle_type || null,
                sharing_price_per_day: form.sharing_enabled && form.sharing_price_per_day !== "" ? parseFloat(form.sharing_price_per_day) : null,
                sharing_min_days: form.sharing_enabled && form.sharing_min_days !== "" ? parseInt(form.sharing_min_days, 10) : null,
                sharing_max_days: form.sharing_enabled && form.sharing_max_days !== "" ? parseInt(form.sharing_max_days, 10) : null,
                sharing_deposit_eur: form.sharing_enabled && form.sharing_deposit_eur !== "" ? parseFloat(form.sharing_deposit_eur) : null,
                sharing_pickup_address: form.sharing_enabled ? (form.sharing_pickup_address || null) : null,
                sharing_available_weekdays: form.sharing_enabled ? (form.sharing_available_weekdays || []) : [],
                // Jun-2026 (User-Wunsch) — Sharing-Tarife Woche / Monat / Saison
                sharing_price_per_week: form.sharing_enabled && form.sharing_price_per_week !== "" ? parseFloat(form.sharing_price_per_week) : null,
                sharing_price_per_month: form.sharing_enabled && form.sharing_price_per_month !== "" ? parseFloat(form.sharing_price_per_month) : null,
                sharing_price_per_season: form.sharing_enabled && form.sharing_price_per_season !== "" ? parseFloat(form.sharing_price_per_season) : null,
                sharing_season_from: form.sharing_enabled ? (form.sharing_season_from || null) : null,
                sharing_season_to: form.sharing_enabled ? (form.sharing_season_to || null) : null,
                // Phase 3.57 — Erweiterte Motorrad-Felder
                cylinder_count: form.cylinder_count !== "" ? parseInt(form.cylinder_count, 10) : null,
                umweltplakette: form.umweltplakette || null,
                features: form.features || [],
                accessories: form.accessories || [],
                warranty_type: form.warranty_type || null,
                last_service_date: form.last_service_date || null,
                workshop_inspected: !!form.workshop_inspected,
                tuev_until: form.tuev_until || null,
                range_km: form.range_km !== "" ? parseInt(form.range_km, 10) : null,
                charging_time_hours: form.charging_time_hours !== "" ? parseFloat(form.charging_time_hours) : null,
                battery_certificate: form.fuel === "Elektro" ? !!form.battery_certificate : null,
                // Phase 3.66 — Werbung & Reichweite
                boost_on_publish: !!form.boost_on_publish,
                publish_to_exchanges: Array.isArray(form.publish_to_exchanges) ? form.publish_to_exchanges : [],
                publish_to_social: Array.isArray(form.publish_to_social) ? form.publish_to_social : [],
                // Phase 3.68 — eBay-Markierung
                ebay_sync_enabled: !!form.ebay_sync_enabled,
                // Phase 3.71 — Sitzanzahl + Käufergruppen-Sichtbarkeit
                seat_count: form.seat_count ? parseInt(form.seat_count, 10) : 2,
                visibility_b2c: form.visibility_b2c !== false,
                visibility_b2b: form.visibility_b2b !== false,
                // Phase 3.72 — Lagerbestand + B2B-Preis + TÜV neu
                // Feb-2026 (User-Wunsch 27.05.2026) — stock_count jetzt nicht
                // mehr nur an Neufahrzeug gekoppelt — wenn stock_state gesetzt
                // ist (Vorlauf/Bestand/Bestell), wird die Menge mitgesendet.
                stock_count: ((form.condition === "Neufahrzeug" || form.stock_state) && form.stock_count !== "")
                    ? parseInt(form.stock_count, 10)
                    : null,
                stock_state: form.stock_state || null,
                price_b2b: form.price_b2b !== "" ? parseFloat(form.price_b2b) : null,
                tuev_neu: !!form.tuev_neu,
                // Phase 3.73 — Sofortmitnahme + MwSt + Standort-Extras
                pickup_available: !!form.pickup_available,
                pickup_with_registration: !!form.pickup_with_registration,
                pickup_unregistered_transport: !!form.pickup_unregistered_transport,
                delivery_nationwide: !!form.delivery_nationwide,
                vat_included: form.vat_included !== false,
                vat_export_net_calculation: !!form.vat_export_net_calculation,
                vat_export_refund_after_docs: !!form.vat_export_refund_after_docs,
                referral_commission_eur: form.referral_commission_eur !== "" && form.referral_commission_eur !== null
                    ? parseInt(form.referral_commission_eur, 10)
                    : null,
                // Mai-2026 (User-Wunsch 21 May Iteration 8) — E-Antrieb / Quad Zusatzfelder
                electric_range_km: form.fuel === "Elektro" && form.electric_range_km !== "" ? parseInt(form.electric_range_km, 10) : null,
                electric_battery_health_pct: form.fuel === "Elektro" && form.electric_battery_health_pct !== "" ? parseInt(form.electric_battery_health_pct, 10) : null,
                electric_battery_type: form.fuel === "Elektro" ? (form.electric_battery_type || null) : null,
                electric_battery_certificate: form.fuel === "Elektro" ? !!form.electric_battery_certificate : false,
                quad_allwheel_drive: form.vehicle_type === "Quad" ? !!form.quad_allwheel_drive : false,
                quad_lof_zulassung: form.vehicle_type === "Quad" ? !!form.quad_lof_zulassung : false,
                quad_towbar: form.vehicle_type === "Quad" ? !!form.quad_towbar : false,
                // Mai-2026 (User-Wunsch 21 May Iteration 13) — Sitzhöhe + Tankinhalt
                seat_height_mm: form.seat_height_mm !== "" ? parseInt(form.seat_height_mm, 10) : null,
                tank_capacity_l: form.tank_capacity_l !== "" ? parseFloat(form.tank_capacity_l) : null,
                stand_platz: form.stand_platz || null,
                schluessel_nummer: form.schluessel_nummer || null,
                leitzahl: form.leitzahl || null,
                key_depot: form.key_depot || null,
                // Phase 3.74 — Portal-Transaktion
                vin: form.vin || null,
                // Jun-2026 (User-Wunsch) — Vorführfahrzeug-Nutzung
                vorfuehr_usage: form.vorfuehr_usage || null,
                vorfuehr_employee_id: form.vorfuehr_usage === "dienstwagen" ? (form.vorfuehr_employee_id || null) : null,
                vorfuehr_employee_name: form.vorfuehr_usage === "dienstwagen" ? (form.vorfuehr_employee_name || null) : null,
                vorfuehr_workshop_site_code: form.vorfuehr_usage === "miet_werkstatt" ? (form.vorfuehr_workshop_site_code || null) : null,
                vorfuehr_workshop_site_name: form.vorfuehr_usage === "miet_werkstatt" ? (form.vorfuehr_workshop_site_name || null) : null,
                vorfuehr_other_text: form.vorfuehr_usage === "anderes" ? (form.vorfuehr_other_text || null) : null,
                key_count: form.key_count !== "" ? parseInt(form.key_count, 10) : null,
                has_handsender: !!form.has_handsender,
                has_keyless_transponder: !!form.has_keyless_transponder,
                has_immobilizer_card: !!form.has_immobilizer_card,
                has_coc: !!form.has_coc,
                has_zb1: !!form.has_zb1,
                has_zb2: !!form.has_zb2,
                document_urls: Array.isArray(form.document_urls) ? form.document_urls : [],
                // Phase 3.86 — Lagerort der Fahrzeugpapiere
                papers_in_house: !!form.papers_in_house,
                papers_in_house_location: form.papers_in_house && form.papers_in_house_location ? String(form.papers_in_house_location).slice(0, 200) : null,
                papers_refinanced: !!form.papers_refinanced,
                papers_refinanced_bank: form.papers_refinanced && form.papers_refinanced_bank ? String(form.papers_refinanced_bank).slice(0, 200) : null,
                papers_refinanced_date: form.papers_refinanced && form.papers_refinanced_date ? form.papers_refinanced_date : null,
                // Phase 3.89 — Versendet an Käufer
                papers_shipped_to_buyer: !!form.papers_shipped_to_buyer,
                papers_shipped_date: form.papers_shipped_to_buyer && form.papers_shipped_date ? form.papers_shipped_date : null,
                // Phase 3.82 — Freitext Service & Wartung
                service_notes: form.service_notes ? String(form.service_notes).slice(0, 2000) : null,
                // Phase 4.20 — Erweiterte ZB1/COC-Stammdaten + Vorbesitzer
                hsn: form.hsn || null,
                tsn: form.tsn || null,
                manufacturer: form.manufacturer || null,
                vehicle_class: form.vehicle_class || null,
                license_plate: form.license_plate || null,
                max_speed_kmh: form.max_speed_kmh !== "" ? parseInt(form.max_speed_kmh, 10) : null,
                length_mm: form.length_mm !== "" ? parseInt(form.length_mm, 10) : null,
                width_mm: form.width_mm !== "" ? parseInt(form.width_mm, 10) : null,
                height_mm: form.height_mm !== "" ? parseInt(form.height_mm, 10) : null,
                weight_kg: form.weight_kg !== "" ? parseInt(form.weight_kg, 10) : null,
                max_weight_kg: form.max_weight_kg !== "" ? parseInt(form.max_weight_kg, 10) : null,
                bemerkungen: form.bemerkungen ? String(form.bemerkungen).slice(0, 1000) : null,
                previous_owner_first_name: form.previous_owner_first_name || null,
                previous_owner_last_name_or_company: form.previous_owner_last_name_or_company || null,
                previous_owner_street: form.previous_owner_street || null,
                previous_owner_house_number: form.previous_owner_house_number || null,
                previous_owner_postal_code: form.previous_owner_postal_code || null,
                previous_owner_city: form.previous_owner_city || null,
            };
            let savedListingId = isEdit ? id : null;
            let watermarkedImages = null;
            if (isEdit) {
                await api.put(`/listings/${id}`, payload);
                toast.success(t("create_listing.toast_updated"));
            } else {
                const { data: created } = await api.post("/listings", payload);
                savedListingId = created?.id || null;
                // Feb-2026 (User-Wunsch 24 May Iter 14): Angebotsnummer prominent
                // im Toast anzeigen — gibt dem Inserent sofortige Bestätigung,
                // dass eine einmalige und unveränderliche Nummer vergeben wurde.
                const num = created?.internal_number;
                const baseMsg = isAuction ? t("create_listing.toast_auction_started") : t("create_listing.toast_published");
                toast.success(num ? `${baseMsg} · Angebotsnummer: ${num}` : baseMsg, { duration: 6000 });
                // Best-effort referral attribution — credits the originating
                // Vermittler with points if a tw_ref cookie/localStorage is set.
                try {
                    const { attributeReferralIfPresent } = await import("../lib/referral");
                    if (created?.id) await attributeReferralIfPresent(api, { reference_id: created.id });
                } catch { /* silent */ }
            }
            // Auto-Wasserzeichen + QR-Code (User-Wunsch): falls der Schalter
            // „Bilder mit Wasserzeichen + QR-Code schützen" aktiv ist (Standard:
            // an), brennen wir Händlername + Angebotsnummer + QR-Code direkt nach
            // dem Speichern in alle Bilder ein. Best-effort — blockiert den Flow
            // nicht, falls das Watermarking fehlschlägt.
            if (savedListingId && form.auto_watermark_on_publish !== false) {
                try {
                    const { data: wm } = await api.post(`/listings/${savedListingId}/apply-watermark`);
                    if (wm?.ok && wm.count > 0) {
                        watermarkedImages = Array.isArray(wm.images) ? wm.images : null;
                        toast.success(
                            `Bildschutz aktiv: Wasserzeichen + QR-Code auf ${wm.count} Bild(ern) angewandt.`,
                            { duration: 5000, id: "auto-watermark" },
                        );
                    }
                } catch (wmErr) {
                    console.warn("Auto-Wasserzeichen übersprungen", wmErr);
                }
            }
            // Phase 3.93 — Erfolgreich gespeichert → Draft wegräumen (Backend + Local)
            await draft.clearAfterPublish();
            // Feb-2026 (User-Wunsch) — Falls Wasserzeichen-Bilder erzeugt wurden,
            // zeige eine Vorschau-Galerie; die Navigation erfolgt erst beim
            // Schließen des Modals. Sonst: bei NEUEN Inseraten den „Als Vorlage
            // speichern?"-Prompt anbieten, danach navigieren.
            const redirect = "/dashboard?tab=listings";
            if (watermarkedImages && watermarkedImages.length && setWatermarkPreview) {
                setWatermarkPreview({ images: watermarkedImages, redirect });
            } else if (!isEdit && onPublishedPromptTemplate) {
                onPublishedPromptTemplate(redirect);
            } else {
                navigate(redirect);
            }
        } catch (err) {
            // Feb-2026 (User-Wunsch): Backend-Validierungsfehler (Pydantic 422)
            // automatisch in das Pflichtfeld-Popup umwandeln, damit der Inserent
            // sofort weiß, wohin springen — anstatt generisches „Speichern fehlgeschlagen".
            const detail = err.response?.data?.detail;
            const status = err.response?.status;
            if (status === 422 && Array.isArray(detail)) {
                // Mapping: Backend-Feldname → { label, selector }
                const FIELD_MAP = {
                    title: { label: "Titel", selector: '[data-testid="form-title"]' },
                    brand: { label: "Marke", selector: '[data-testid="form-brand"]' },
                    model: { label: "Modell", selector: '[data-testid="form-model"]' },
                    vehicle_type: { label: "Fahrzeugtyp", selector: '[data-testid="form-vehicle-type"]' },
                    bike_type: { label: "Fahrradtyp", selector: '[data-testid="form-bike-type"]' },
                    condition: { label: "Zustand", selector: '[data-testid="form-condition"]' },
                    price: { label: "Preis B2C", selector: '[data-testid="form-price"]' },
                    price_b2b: { label: "Preis B2B", selector: '[data-testid="form-price-b2b"]' },
                    referral_commission_eur: { label: "Vermittlungsprovision", selector: '[data-testid="form-referral-commission"]' },
                    first_registration_date: { label: "Erstzulassung", selector: '[data-testid="form-erstzulassung"], [data-testid="form-first-registration"]' },
                    year: { label: "Erstzulassung (Jahr)", selector: '[data-testid="form-year"], [data-testid="form-erstzulassung"]' },
                    previous_owners: { label: "Anzahl Vorbesitzer", selector: '[data-testid="form-previous-owners"]' },
                    mileage: { label: "Kilometerstand", selector: '[data-testid="form-mileage"]' },
                    color_base: { label: "Farbbasis", selector: '[data-testid="form-color-base"]' },
                    displacement: { label: "Hubraum (ccm)", selector: '[data-testid="form-displacement"]' },
                    power_kw: { label: "Leistung (kW)", selector: '[data-testid="listing-power-kw"]' },
                    drive_type: { label: "Antrieb", selector: '[data-testid="listing-drive-type"]' },
                    fuel: { label: "Kraftstoff", selector: '[data-testid="listing-fuel"]' },
                    transmission: { label: "Getriebe", selector: '[data-testid="listing-transmission"]' },
                    images: { label: "Bilder (≥ 1)", selector: '[data-testid="image-upload-section"]' },
                    description: { label: "Beschreibung", selector: '[data-testid="form-description"]' },
                    location: { label: "Standort", selector: '[data-testid="form-location"]' },
                };
                // Feb-2026 (User-Wunsch): Pydantic-Fehlertypen ins Deutsche übersetzen
                // damit Inserent statt „Input should be less than or equal to 50000"
                // eine verständliche Meldung sieht.
                const translatePydanticError = (d, fieldLabel) => {
                    const ctx = d.ctx || {};
                    switch (d.type) {
                        case "missing":
                            return `${fieldLabel} ist ein Pflichtfeld`;
                        case "string_too_short":
                            return `${fieldLabel} ist zu kurz (mindestens ${ctx.min_length ?? "n"} Zeichen)`;
                        case "string_too_long":
                            return `${fieldLabel} ist zu lang (maximal ${ctx.max_length ?? "n"} Zeichen)`;
                        case "greater_than":
                            return `${fieldLabel} muss größer als ${ctx.gt} sein`;
                        case "greater_than_equal":
                            return `${fieldLabel} muss mindestens ${ctx.ge} betragen`;
                        case "less_than":
                            return `${fieldLabel} muss kleiner als ${ctx.lt} sein`;
                        case "less_than_equal":
                            return `${fieldLabel} darf maximal ${ctx.le} betragen`;
                        case "int_parsing":
                        case "float_parsing":
                            return `${fieldLabel} muss eine Zahl sein`;
                        case "value_error":
                            return `${fieldLabel}: ${d.msg.replace(/^Value error,?\s*/i, "")}`;
                        case "enum":
                            return `${fieldLabel}: Auswahl ungültig`;
                        case "url_parsing":
                            return `${fieldLabel}: ungültige URL`;
                        case "date_from_datetime_parsing":
                        case "date_parsing":
                            return `${fieldLabel}: ungültiges Datum`;
                        default:
                            return `${fieldLabel}: ${d.msg || "ungültig"}`;
                    }
                };
                const backendMissing = [];
                detail.forEach((d) => {
                    const fieldName = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : null;
                    if (!fieldName) return;
                    const mapped = FIELD_MAP[fieldName];
                    const label = mapped?.label || fieldName;
                    const selector = mapped?.selector || "";
                    backendMissing.push({
                        key: fieldName,
                        label: translatePydanticError(d, label),
                        selector,
                    });
                });
                if (backendMissing.length > 0) {
                    setBackendValidationErrors(backendMissing);
                    setMissingDialogOpen(true);
                    toast.error(`Eingabe prüfen: ${backendMissing.map((m) => m.label).join(" · ")}`, {
                        duration: 8000,
                        id: "backend-validation",
                    });
                    return;
                }
            }
            toast.error(typeof detail === "string" ? detail : t("create_listing.save_failed"));
        } finally {
            setSubmitting(false);
        }
}
